/**
 * LUXWORLD Sheer Curtains Collection V2
 * Asset: lux-sheer-collection-v2.js
 * Version: 2.1.0
 */
(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-lux-sheer-collection-v2]";
  const INITIALISED_ATTRIBUTE = "data-lux-sc-initialised";
  const MOBILE_BREAKPOINT = 680;

  function parseJSON(value, fallback = {}) {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.warn("[LUXWORLD Sheer Collection V2] Invalid JSON data.", error);
      return fallback;
    }
  }

  function readSectionData(root) {
    const dataScript = root.querySelector('script[id^="LuxSheerCollectionV2Data-"]');
    if (!dataScript) return {};
    return parseJSON(dataScript.textContent || "{}", {});
  }

  function trackEvent(root, eventName, details = {}) {
    if (!eventName) return;

    const data = root.__luxSheerCollectionData || {};
    const payload = {
      event: eventName,
      page_type: data?.analytics?.pageType || "sheer-curtains-collection-v2",
      collection_handle: data?.analytics?.collectionHandle || "design-studio-sheer",
      ...details
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    try {
      root.dispatchEvent(
        new CustomEvent("luxworld:sheer-collection-event", {
          bubbles: true,
          detail: payload
        })
      );
    } catch (error) {
      console.warn("[LUXWORLD Sheer Collection V2] Event dispatch failed.", error);
    }
  }

  function activateTab(tabs, panels, selectedTab, getTabKey, getPanelKey, focusTab = false) {
    const selectedKey = getTabKey(selectedTab);
    if (!selectedKey) return;

    tabs.forEach((tab) => {
      const active = tab === selectedTab;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-selected", active ? "true" : "false");
      tab.setAttribute("tabindex", active ? "0" : "-1");
    });

    panels.forEach((panel) => {
      const active = getPanelKey(panel) === selectedKey;
      panel.classList.toggle("is-active", active);
      panel.hidden = !active;
      panel.setAttribute("aria-hidden", active ? "false" : "true");
      panel.setAttribute("tabindex", active ? "0" : "-1");
    });

    if (focusTab) selectedTab.focus({ preventScroll: true });
  }

  function bindTabKeyboardNavigation(tabList, tabs, activate) {
    tabList.addEventListener("keydown", (event) => {
      const currentIndex = tabs.indexOf(document.activeElement);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;
      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case "ArrowLeft":
        case "ArrowUp":
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      activate(tabs[nextIndex], true);
    });
  }

  function initialiseSceneTabs(root) {
    root.querySelectorAll("[data-lux-sc-scene]").forEach((scene) => {
      if (!(scene instanceof HTMLElement) || scene.dataset.luxScSceneReady === "true") return;

      const tabList = scene.querySelector('[role="tablist"]');
      const tabs = Array.from(scene.querySelectorAll("[data-lux-sc-scene-mode]")).filter(
        (item) => item instanceof HTMLElement
      );
      const panels = Array.from(scene.querySelectorAll("[data-lux-sc-scene-panel]")).filter(
        (item) => item instanceof HTMLElement
      );

      if (!(tabList instanceof HTMLElement) || !tabs.length || !panels.length) return;

      const activate = (tab, focusTab = false) => {
        activateTab(
          tabs,
          panels,
          tab,
          (item) => item.dataset.luxScSceneMode,
          (item) => item.dataset.luxScScenePanel,
          focusTab
        );

        trackEvent(root, "sheer_curtain_scene_changed", {
          scene_mode: tab.dataset.luxScSceneMode || ""
        });
      };

      tabs.forEach((tab) => tab.addEventListener("click", () => activate(tab)));
      bindTabKeyboardNavigation(tabList, tabs, activate);

      const activeTab = tabs.find((tab) => tab.getAttribute("aria-selected") === "true") || tabs[0];
      activateTab(
        tabs,
        panels,
        activeTab,
        (item) => item.dataset.luxScSceneMode,
        (item) => item.dataset.luxScScenePanel
      );

      scene.dataset.luxScSceneReady = "true";
    });
  }

  function initialiseRoomTabs(root) {
    root.querySelectorAll("[data-lux-sc-room-explorer]").forEach((explorer) => {
      if (!(explorer instanceof HTMLElement) || explorer.dataset.luxScRoomReady === "true") return;

      const tabList = explorer.querySelector('[role="tablist"]');
      const tabs = Array.from(explorer.querySelectorAll("[data-lux-sc-room-tab]")).filter(
        (item) => item instanceof HTMLElement
      );
      const panels = Array.from(explorer.querySelectorAll("[data-lux-sc-room-panel]")).filter(
        (item) => item instanceof HTMLElement
      );

      if (!(tabList instanceof HTMLElement) || !tabs.length || !panels.length) return;

      const activate = (tab, focusTab = false) => {
        activateTab(
          tabs,
          panels,
          tab,
          (item) => item.dataset.luxScRoomTab,
          (item) => item.dataset.luxScRoomPanel,
          focusTab
        );

        tab.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "nearest",
          inline: "center"
        });

        trackEvent(root, "sheer_curtain_room_changed", {
          room_key: tab.dataset.luxScRoomTab || "",
          room_name: (tab.textContent || "").trim()
        });
      };

      tabs.forEach((tab) => tab.addEventListener("click", () => activate(tab)));
      bindTabKeyboardNavigation(tabList, tabs, activate);

      const activeTab = tabs.find((tab) => tab.getAttribute("aria-selected") === "true") || tabs[0];
      activateTab(
        tabs,
        panels,
        activeTab,
        (item) => item.dataset.luxScRoomTab,
        (item) => item.dataset.luxScRoomPanel
      );

      explorer.dataset.luxScRoomReady = "true";
    });
  }

  function initialiseFaq(root) {
    root.querySelectorAll("[data-lux-sc-faq]").forEach((list) => {
      if (!(list instanceof HTMLElement) || list.dataset.luxScFaqReady === "true") return;

      const detailsItems = Array.from(list.querySelectorAll("details"));
      detailsItems.forEach((details) => {
        details.addEventListener("toggle", () => {
          if (!details.open) return;
          detailsItems.forEach((other) => {
            if (other !== details) other.open = false;
          });

          const summary = details.querySelector("summary");
          trackEvent(root, "sheer_curtain_faq_opened", {
            question: (summary?.textContent || "").trim()
          });
        });
      });

      list.dataset.luxScFaqReady = "true";
    });
  }

  function initialiseCtaTracking(root) {
    root.querySelectorAll("[data-lux-sc-event], [data-lux-sc-builder-link]").forEach((element) => {
      if (!(element instanceof HTMLElement) || element.dataset.luxScTrackingReady === "true") return;

      element.addEventListener("click", () => {
        const eventName =
          element.dataset.luxScEvent ||
          (element.hasAttribute("data-lux-sc-builder-link")
            ? "sheer_curtain_builder_enter"
            : "sheer_curtain_cta_click");

        trackEvent(root, eventName, {
          label: (element.textContent || "").trim(),
          destination: element instanceof HTMLAnchorElement ? element.href : "",
          product_handle: element.dataset.productHandle || ""
        });
      });

      element.dataset.luxScTrackingReady = "true";
    });
  }

  function initialiseMobileCta(root) {
    const mobileCta = root.querySelector("[data-lux-sc-mobile-cta]");
    if (!(mobileCta instanceof HTMLElement) || mobileCta.dataset.luxScMobileReady === "true") return;

    const hero = root.querySelector(".lux-dc-v2__hero");
    const finalCta = root.querySelector(".lux-dc-v2__final-cta");
    let heroVisible = true;
    let finalVisible = false;

    const update = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      const shouldHide = !mobile || heroVisible || finalVisible;
      mobileCta.classList.toggle("is-hidden", shouldHide);
      mobileCta.setAttribute("aria-hidden", shouldHide ? "true" : "false");
    };

    if ("IntersectionObserver" in window) {
      if (hero instanceof HTMLElement) {
        const heroObserver = new IntersectionObserver(
          (entries) => {
            heroVisible = entries.some((entry) => entry.isIntersecting);
            update();
          },
          { threshold: 0.05 }
        );
        heroObserver.observe(hero);
      } else {
        heroVisible = false;
      }

      if (finalCta instanceof HTMLElement) {
        const finalObserver = new IntersectionObserver(
          (entries) => {
            finalVisible = entries.some((entry) => entry.isIntersecting);
            update();
          },
          { threshold: 0.12 }
        );
        finalObserver.observe(finalCta);
      }
    } else {
      heroVisible = false;
    }

    window.addEventListener("resize", update, { passive: true });
    update();
    mobileCta.dataset.luxScMobileReady = "true";
  }

  function announce(root, message) {
    const region = root.querySelector("[data-lux-sc-announcement]");
    if (!(region instanceof HTMLElement) || !message) return;

    region.textContent = "";
    window.setTimeout(() => {
      region.textContent = message;
    }, 40);

    window.setTimeout(() => {
      if (region.textContent === message) region.textContent = "";
    }, 3500);
  }

  function initialiseRoot(root) {
    if (!(root instanceof HTMLElement) || root.getAttribute(INITIALISED_ATTRIBUTE) === "true") return;

    root.__luxSheerCollectionData = readSectionData(root);

    initialiseSceneTabs(root);
    initialiseRoomTabs(root);
    initialiseFaq(root);
    initialiseCtaTracking(root);
    initialiseMobileCta(root);

    root.addEventListener("luxworld:sheer-collection-announce", (event) => {
      announce(root, String(event.detail?.message || ""));
    });

    root.setAttribute(INITIALISED_ATTRIBUTE, "true");

    trackEvent(root, "sheer_curtain_collection_loaded", {
      total_collections: root.__luxSheerCollectionData?.counts?.totalCollections || 0,
      total_colours: root.__luxSheerCollectionData?.counts?.totalColours || 0
    });
  }

  function initialiseAll(context = document) {
    const roots = [];

    if (context instanceof HTMLElement && context.matches(ROOT_SELECTOR)) roots.push(context);
    context.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
      if (root instanceof HTMLElement) roots.push(root);
    });

    roots.forEach(initialiseRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialiseAll(document), { once: true });
  } else {
    initialiseAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target instanceof HTMLElement) initialiseAll(event.target);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const block = event.target;
    if (!(block instanceof HTMLElement)) return;

    const root = block.closest(ROOT_SELECTOR);
    if (!(root instanceof HTMLElement)) return;

    const roomPanel = block.closest("[data-lux-sc-room-panel]");
    if (roomPanel instanceof HTMLElement) {
      const key = roomPanel.dataset.luxScRoomPanel || "";
      const escapedKey = window.CSS?.escape ? CSS.escape(key) : key.replace(/["\\]/g, "\\$&");
      const tab = root.querySelector(`[data-lux-sc-room-tab="${escapedKey}"]`);
      if (tab instanceof HTMLElement) tab.click();
    }

    const scenePanel = block.closest("[data-lux-sc-scene-panel]");
    if (scenePanel instanceof HTMLElement) {
      const key = scenePanel.dataset.luxScScenePanel || "";
      const escapedKey = window.CSS?.escape ? CSS.escape(key) : key.replace(/["\\]/g, "\\$&");
      const tab = root.querySelector(`[data-lux-sc-scene-mode="${escapedKey}"]`);
      if (tab instanceof HTMLElement) tab.click();
    }

    block.scrollIntoView({ behavior: "smooth", block: "center" });
  });
})();
